import * as turf from '@turf/turf';
import { Flag32 as Flag } from '@weston/react-world-flags';
import { useTranslation } from 'react-i18next';
import { useSnapshot } from 'valtio';

import { Box, LinearProgress, Typography } from '@mui/material';

import BoltIcon from '@mui/icons-material/Bolt';
import EnergySavingsLeafIcon from '@mui/icons-material/EnergySavingsLeaf';

import { store } from '../../store';

export const TripFooter = () => {
  const { t } = useTranslation();
  const { selectedLegs, tripFooter } = useSnapshot(store);

  // Si aucune jambe n'est sélectionnée
  if (selectedLegs.length === 0) return null;

  const startLeg = selectedLegs.at(0);
  const endLeg = selectedLegs.at(-1);

  if (!startLeg || !endLeg) return null;

  // Progression simulée
  const progress = tripFooter.progress; // Remplacer par une vraie logique si nécessaire

  return (
    <Box
      sx={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: 'rgba(255, 255, 255, 1)',
        zIndex: 1000,
        padding: '10px 20px',
        boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      {/* Informations sur le départ */}
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Flag code={startLeg.start_iso} height={20} />
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            backgroundColor: 'green',
            ml: 1,
          }}
        />
        <Box sx={{ ml: 1 }}>
          <Typography variant="body2" fontWeight={500}>
            {startLeg.start_location}
          </Typography>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('reeferInsights.map.origin')}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block' }}
            >
              {new Date(startLeg.start_datetime).toLocaleString(undefined, {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
              })}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Barre de progression */}
      <Box sx={{ flex: 1, mx: 2, textAlign: 'center' }}>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 8,
            borderRadius: 4,
            '& .MuiLinearProgress-bar': {
              backgroundColor: 'green',
            },
          }}
        />
        <Typography
          variant="caption"
          color="text.secondary"
          textAlign="center"
          sx={{ mt: 0.5 }}
        >
          {`${t('reeferInsights.map.progress')}: ${progress.toFixed(0)}%`}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            mt: 0.5,
          }}
        >
          <EnergySavingsLeafIcon
            fontSize="small"
            sx={{ mr: 0.5, color: '#43a047' }}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            textAlign="center"
            sx={{ mr: 2 }}
          >
            {`${t('reeferInsights.map.totalCo2e')}: ${startLeg.co2eq_kg ?? 0} kg`}
          </Typography>
          <BoltIcon fontSize="small" sx={{ mr: 0.5, color: '#fbc02d' }} />
          <Typography
            variant="caption"
            color="text.secondary"
            textAlign="center"
          >
            {`${t('reeferInsights.map.totalEnergy')}: ${startLeg.active_energy_kwh ?? 0} kWh`}
          </Typography>
        </Box>
      </Box>

      {/* Informations sur l'arrivée */}
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            backgroundColor: 'red',
            mr: 1,
          }}
        />
        <Box sx={{ mr: 1, textAlign: 'right' }}>
          <Typography variant="body2" fontWeight={500}>
            {endLeg.end_location}
          </Typography>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('reeferInsights.map.destination')}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block' }}
            >
              {new Date(endLeg.end_datetime).toLocaleString(undefined, {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
              })}
            </Typography>
          </Box>
        </Box>
        <Flag code={endLeg.end_iso} height={20} />
      </Box>
    </Box>
  );
};
